import Vue from 'vue';

export default {
    /**
     * Set players
     *
     * @param {object} state    Vuex state.
     * @param {object}  payload    Array of players objects.
     * @param {string} payload.type     Player type.
     * @param {object} payload.data     Player data
     * @param {boolean} payload.replace  Whether to replace or merge data
     */
    setPlayers(state, { type, data, replace = true }) {
        if (replace) {
            state[`${type}Players`] = {
                ...state[`${type}Players`],
                ...data,
            };
        } else {
            // Merge with existing data for pagination
            Object.keys(data).forEach((showHash) => {
                state[`${type}Players`][showHash] = {
                    ...state[`${type}Players`]?.[showHash],
                    ...data[showHash],
                };
            });
        }
    },

    /**
     * Set players pagination info
     *
     * @param {object} state    Vuex state.
     * @param {object} payload    Pagination data.
     * @param {string} payload.type     Player type.
     * @param {string} payload.showHash Show hash.
     * @param {object} payload.pagination Pagination metadata.
     */
    setPlayersPagination(state, { type, showHash, pagination }) {
        if (! state[`${type}PlayersPagination`]) {
            Vue.set(state, `${type}PlayersPagination`, {});
        }

        Vue.set(state[`${type}PlayersPagination`], showHash, pagination);
    },

    /**
     * Set a specific page state (data, loading, error)
     *
     * @param {object} state    Vuex state.
     * @param {object} payload  Payload data.
     * @param {string} payload.type     Player type.
     * @param {string} payload.showHash Show hash.
     * @param {number} payload.page     Page number.
     * @param {object} payload.data     Player data for this page (optional).
     * @param {boolean} payload.loading Loading state (optional).
     * @param {string|null} payload.error Error message (optional).
     */
    setPlayersPageState(state, { type, showHash, page, data, loading, error }) {
        if (! state[`${type}Players`]) {
            Vue.set(state, `${type}Players`, {});
        }

        if (! state[`${type}Players`][showHash]) {
            Vue.set(state[`${type}Players`], showHash, []);
        }

        // Find or create page object
        let pageObj = state[`${type}Players`][showHash].find(p => p.page === page);

        if (! pageObj) {
            pageObj = { page, loading: false, error: null, players: {} };
            state[`${type}Players`][showHash].push(pageObj);
        }

        // Update properties if provided
        if (data !== undefined) {
            pageObj.players = data;
        }
        if (loading !== undefined) {
            pageObj.loading = loading;
        }
        if (error !== undefined) {
            pageObj.error = error;
        }
    },

    /**
     * Add player
     *
     * @param {object}  state               Vuex state.
     * @param {object}  payload              Array of players objects.
     * @param {string}  payload.showHash     Show hash.
     * @param {object}  payload.data        Player data.
     */
    addPlayer(state, { showHash, data }) {
        state.archivePlayers[showHash] = {
            ...state.archivePlayers[showHash],
            ...data,
        };
    },

    /**
     * Delete player
     *
     * @param {object}  state               Vuex state.
     * @param {object}  payload              Array of players objects.
     * @param {string}  payload.showHash     Show hash.
     * @param {string}  payload.playerHash   Player hash.
     * @param {string}  payload.type         Player type.
     */
    deletePlayer(state, { showHash, playerHash, type }) {
        Vue.delete(state[`${type}Players`][showHash], [playerHash]);
    },

    /**
     * Update player
     *
     * @param {object}  state               Vuex state.
     * @param {object}  payload              Array of players objects.
     * @param {string}  payload.showHash     Show hash.
     * @param {string}  payload.playerKey    Player Key.
     * @param {string}  payload.type         Player type.
     * @param {object}  payload.data         Player object.
     */
    updatePlayer(state, { showHash, playerKey, type, data }) {
        state[`${type}Players`][showHash][playerKey] = {
            ...state[`${type}Players`][showHash][playerKey],
            ...data,
        };
    },

    /**
     * Set track player config
     *
     * @param {object}  state               Vuex state.
     * @param {object}  payload              Payload data.
     * @param {string}  payload.showHash     Show hash.
     * @param {string}  payload.trackNo      Player track number.
     * @param {object}  payload.data         Player config object.
     */
    setTrackPlayerConfig(state, { showHash, trackNo, data }) {
        if (! state.trackPlayersConfig[showHash]) {
            Vue.set(state.trackPlayersConfig, showHash, {});
        }

        state.trackPlayersConfig[showHash] = {
            ...state.trackPlayersConfig[showHash],
            [trackNo]: data,
        };
    },

    /**
     * Update track player config
     *
     * @param {object}  state               Vuex state.
     * @param {object}  payload              Payload data.
     * @param {string}  payload.showHash     Show hash.
     * @param {string}  payload.trackNo      Player track number.
     * @param {object}  payload.data         Player config object.
     */
    updateTrackPlayerConfig(state, { showHash, trackNo, data }) {
        state.trackPlayersConfig[showHash][trackNo] = {
            ...state.trackPlayersConfig[showHash][trackNo],
            ...data,
        };
    },

    /**
     * Add recent players
     *
     * @param {object}  state               Vuex state.
     * @param {object}  payload              Payload data.
     * @param {string}  payload.type         Players type.
     * @param {string}  payload.showHash     Show hash.
     * @param {string}  payload.players     Array with players.
     */
    addRecentPlayers(state, { type, showHash, players }) {
        if (! state[`${type}RecentPlayers`]?.[showHash]) {
            Vue.set(state[`${type}RecentPlayers`], showHash, []);
        }

        state[`${type}RecentPlayers`][showHash] = [...players];
    },

    /**
     * Clears recent players
     *
     * @param {object}  state               Vuex state.
     * @param {object}  payload              Payload data.
     * @param {string}  payload.type         Players type.
     */
    clearRecentPlayers(state, { type }) {
        state[`${type}RecentPlayers`] = {};
    },
};
